/* Added the following code for POW-NOTIFY.Istiyak using AI Agent. April 5, 2026. Code Start */
/**
 * STOMP over a native WebSocket to Spring (`/ws` endpoint).
 * Matches a Spring endpoint registered with .addEndpoint("/ws") and **without** .withSockJS().
 */
import { Client } from '@stomp/stompjs';

/**
 * Builds ws:// or wss:// URL from http(s)://…/ws (or returns ws URL unchanged).
 */
export function toStompBrokerWebSocketUrl(httpOrWsUrl) {
	const u = new URL(httpOrWsUrl);
	if (u.protocol === 'ws:' || u.protocol === 'wss:') {
		return `${u.protocol}//${u.host}${u.pathname}`.replace(/\/$/, '') || httpOrWsUrl;
	}
	const wsScheme = u.protocol === 'https:' ? 'wss' : 'ws';
	const path = u.pathname.replace(/\/$/, '') || '/ws';
	return `${wsScheme}://${u.host}${path}`;
}

/**
 * @param {object} options
 * @param {string} options.brokerWsUrl - STOMP WebSocket URL: `ws://host/ws` or `http://host/ws` (converted to ws/wss)
 * @param {(notification: object) => void} options.onNotification
 * @param {() => Record<string, string>} [options.getConnectHeaders] - STOMP CONNECT (e.g. Authorization: Bearer …)
 * @param {(state: string) => void} [options.onConnectionState] - 'connected' | 'closed'
 * @returns {import('@stomp/stompjs').Client}
 */
export function createNotificationsStompClient({
	brokerWsUrl,
	onNotification,
	getConnectHeaders,
	onConnectionState
}) {
	const brokerURL = toStompBrokerWebSocketUrl(brokerWsUrl);

	const client = new Client({
		brokerURL,
		reconnectDelay: 5000,
		heartbeatIncoming: 15000,
		heartbeatOutgoing: 15000
	});

	if (typeof getConnectHeaders === 'function') {
		client.beforeConnect = () => {
			client.connectHeaders = getConnectHeaders();
		};
	}

	client.onConnect = () => {
		if (onConnectionState) {
			onConnectionState('connected');
		}
		const handleIncomingMessage = message => {
			try {
				if (!message || typeof message.body !== 'string' || !message.body.trim()) {
					return;
				}
				const parsed = JSON.parse(message.body);
				const payload = parsed && typeof parsed === 'object' ? (parsed.notification || parsed.data || parsed) : null;
				if (!payload) {
					return;
				}
				if (Array.isArray(payload)) {
					payload.forEach(item => {
						if (item && typeof item === 'object') {
							onNotification(item);
						}
					});
					return;
				}
				if (typeof payload === 'object') {
					onNotification(payload);
				}
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error('Notifications STOMP: invalid JSON body', e);
			}
		};

		/* Keep /topic subscription as requested, and also listen to user queue for backend compatibility. */
		client.subscribe('/topic/notifications', handleIncomingMessage);
		client.subscribe('/user/queue/notifications', handleIncomingMessage);
	};

	client.onWebSocketClose = () => {
		if (onConnectionState) {
			onConnectionState('closed');
		}
	};

	client.onStompError = frame => {
		// eslint-disable-next-line no-console
		console.error('Notifications STOMP error', frame.headers.message, frame.body);
	};

	return client;
}
/* Code end. Istiyak using AI Agent. April 5, 2026 */
